import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { supabase } from '../../utils/supabase';

/**
 * Component that handles authentication-based redirects
 * Used to redirect users after successful authentication
 * 
 * This component directly fetches user preferences instead of relying on
 * the UserProvider to avoid race conditions during OAuth redirects.
 */
export function AuthRedirect() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isChecking, setIsChecking] = useState(true);

  useEffect(() => {
    const checkUserAndRedirect = async () => {
      // Wait for auth to finish loading
      if (authLoading) {
        return;
      }

      // No user means auth failed or user logged out
      if (!user) {
        navigate('/login', { replace: true });
        return;
      }

      try {
        // Directly fetch user preferences to check for selected player
        const { data, error } = await supabase
          .from('user_preferences')
          .select('selected_player_id')
          .eq('user_id', user.id)
          .single();

        // Get the intended destination from location state
        const from = (location.state as { from?: { pathname?: string } })?.from?.pathname;

        // If user came from a protected route (not login/home/select-player), redirect there
        if (from && from !== '/login' && from !== '/' && from !== '/select-player') {
          console.log('AuthRedirect: navigating to previous route:', from);
          navigate(from, { replace: true });
        } else if (!error && data?.selected_player_id) {
          // User has a selected player, go to dashboard
          console.log('AuthRedirect: user has player, navigating to dashboard');
          navigate('/dashboard', { replace: true });
        } else {
          // No player selected or error fetching, go to player selection
          console.log('AuthRedirect: no player selected, navigating to select-player');
          navigate('/select-player', { replace: true });
        }
      } catch (err) {
        console.error('AuthRedirect: error checking user preferences:', err);
        // On error, default to player selection
        navigate('/select-player', { replace: true });
      } finally {
        setIsChecking(false);
      }
    };

    checkUserAndRedirect();
  }, [user, authLoading, navigate, location.state]);

  // Show nothing while checking - the redirect will happen quickly
  if (isChecking || authLoading) {
    return null;
  }

  return null;
}